import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Grid from '@mui/material/Grid';
import { Box } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { SelectedFilters } from 'components/organisms/SelectedFilters';
import { useTranslations } from 'hooks/useTranslations';
import { FormDatePicker } from 'components/molecules/FormDatePicker';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import { IAccessoriesTransactionsFormValues } from 'models/ancillaries';
import { useLang } from 'models/langContext';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import { EDictionaryTypes } from 'models/dictionary';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { FormSelect } from 'components/molecules/FormSelect';
import { prepareFilters } from 'helpers/filters';
import { ESearchFilterKeys } from 'constants/SearchFilterTypes';
import { formatDateTimeToIso } from 'utils/date';
import { EFirearmStateId } from 'models/firearm';

const initialFormValues: IAccessoriesTransactionsFormValues = {
  stateUniqueId: '',
  transactionDateFrom: '',
  transactionDateTo: '',
};

enum EAccessoryTransactionsLabel {
  stateUniqueId = 'accessoryDetails.transactions.state.label',
  transactionDateFrom = 'accessoryDetails.transactions.transactionDateFrom.label',
  transactionDateTo = 'accessoryDetails.transactions.transactionDateTo.label',
}

interface IAccessoriesTransactionsSearchForm {
  onSubmit: (arg: IAccessoriesTransactionsFormValues) => void;
}

const AccessoriesTransactionsSearchForm = ({
  onSubmit,
}: IAccessoriesTransactionsSearchForm) => {
  const { t } = useTranslations();
  const {
    control,
    handleSubmit,
    watch,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<IAccessoriesTransactionsFormValues>({
    defaultValues: initialFormValues,
  });
  const [values, setValues] = useState<IAccessoriesTransactionsFormValues>(initialFormValues);
  const { selectedLanguage } = useLang();

  const { loadDictionaries, state } = useAncillaries();

  useEffect(() => {
    const subscription = watch((value) => {
      setValues(value as IAccessoriesTransactionsFormValues);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.State]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const handleResetField = (name?: string, refreshResults: boolean = true) => {
    if (name) {
      reset({ ...getValues(), [name]: '' });
    } else {
      reset(initialFormValues);
    }

    if (refreshResults) {
      onSubmit(getValues());
    }
  };

  const submitForm = (data: IAccessoriesTransactionsFormValues) => {
    if (data.transactionDateFrom) {
      data.transactionDateFrom = formatDateTimeToIso(data.transactionDateFrom);
    } else {
      delete data?.transactionDateFrom;
    }
    if (data.transactionDateTo) {
      data.transactionDateTo = formatDateTimeToIso(data.transactionDateTo, true);
    } else {
      delete data?.transactionDateTo;
    }
    onSubmit(data);
  };

  return (
    <SearchWrapper>
      <form onSubmit={handleSubmit(submitForm)}>
        <Grid container columnSpacing={2} rowSpacing={2} sx={{ justifyContent: 'flex-start' }}>
          <Grid size={{ xs: 12, md: 3, sm: 12 }}>
            <FormSelect
              options={mapDictionaryToOptionProp(state, undefined, [
                EFirearmStateId.EXPIRED,
                EFirearmStateId.DISABLED,
              ])}
              name="stateUniqueId"
              label={t(EAccessoryTransactionsLabel.stateUniqueId)}
              canBeEmpty
              control={control}
              errors={errors}
              setValue={setValue}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3, sm: 12 }}>
            <FormDatePicker
              name="transactionDateFrom"
              label={t(EAccessoryTransactionsLabel.transactionDateFrom)}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3, sm: 12 }}>
            <FormDatePicker
              name="transactionDateTo"
              label={t(EAccessoryTransactionsLabel.transactionDateTo)}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3, sm: 12 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant={EButtonVariants.contained}
                label={t('ancillaries.accordion.search.button')}
                type="submit"
                fullWidth
                size={EButtonSizes.small}
                alignWithInputSX={{ xs: 12, md: 3 }}
              />
            </Box>
          </Grid>
        </Grid>
      </form>
      <SelectedFilters
        name="accessories_transaction"
        values={values}
        labels={EAccessoryTransactionsLabel}
        type={ESearchFilterKeys.ancillaries}
        dictionaries={{
          stateUniqueId: prepareFilters(state, 'uniqueId', 'name'),
        }}
        handleDelete={handleResetField}
        showSaveFilters={false}
        saveRouterStateAfterClearAll={false}
      />
      <Box sx={{ mt: 2 }} />
    </SearchWrapper>
  );
};

export { AccessoriesTransactionsSearchForm };
